import { ErrorMessage } from '@occc-app/lib/ErrorMessage';

/* Action Types */

const AUDIENCE_COMPONENT_ERR = 'hq/Offer/Audience/AUDIENCE_COMPONENT_ERR';
const AUDIENCE_COMPONENT_OK = 'hq/Offer/Audience/AUDIENCE_COMPONENT_OK';
const AUDIENCE_COMPONENTS_ERR = 'hq/Offer/Audience/AUDIENCE_COMPONENTS_ERR';
const AUDIENCE_COMPONENTS_OK = 'hq/Offer/Audience/AUDIENCE_COMPONENTS_OK';
const AUDIENCE_CONFIGURATION_UPDATE = 'hq/Offer/Audience/AUDIENCE_CONFIGURATION_UPDATE';
const AUDIENCE_TOGGLE_UPDATE = 'hq/Offer/Audience/AUDIENCE_TOGGLE_UPDATE';
const REQUEST_AUDIENCE = 'hq/Offer/Audience/REQUEST_AUDIENCE';
const REQUEST_AUDIENCE_ERR = 'hq/Offer/Audience/REQUEST_AUDIENCE_ERR';
const REQUEST_AUDIENCE_OK = 'hq/Offer/Audience/REQUEST_AUDIENCE_OK';
const REQUEST_AUDIENCES_OK = 'hq/Offer/Audience/REQUEST_AUDIENCES_OK';
const REQUEST_SEGMENTS = 'hq/Offer/Audience/REQUEST_SEGMENTS';
const REQUEST_SEGMENTS_ERR = 'hq/Offer/Audience/REQUEST_SEGMENTS_ERR';
const REQUEST_SEGMENTS_OK = 'hq/Offer/Audience/REQUEST_SEGMENTS_OK';
const RESET_INITIAL_AUDIENCES_STATE = 'hq/Offer/Audience/RESET_INITIAL_AUDIENCES_STATE';

type Types = {
  AUDIENCE_COMPONENT_ERR: typeof AUDIENCE_COMPONENT_ERR;
  AUDIENCE_COMPONENT_OK: typeof AUDIENCE_COMPONENT_OK;
  AUDIENCE_COMPONENTS_ERR: typeof AUDIENCE_COMPONENTS_ERR;
  AUDIENCE_COMPONENTS_OK: typeof AUDIENCE_COMPONENTS_OK;
  AUDIENCE_CONFIGURATION_UPDATE: typeof AUDIENCE_CONFIGURATION_UPDATE;
  AUDIENCE_TOGGLE_UPDATE: typeof AUDIENCE_TOGGLE_UPDATE;
  REQUEST_AUDIENCE: typeof REQUEST_AUDIENCE;
  REQUEST_AUDIENCE_ERR: typeof REQUEST_AUDIENCE_ERR;
  REQUEST_AUDIENCE_OK: typeof REQUEST_AUDIENCE_OK;
  REQUEST_AUDIENCES_OK: typeof REQUEST_AUDIENCES_OK;
  REQUEST_SEGMENTS: typeof REQUEST_SEGMENTS;
  REQUEST_SEGMENTS_ERR: typeof REQUEST_SEGMENTS_ERR;
  REQUEST_SEGMENTS_OK: typeof REQUEST_SEGMENTS_OK;
  RESET_INITIAL_AUDIENCES_STATE: typeof RESET_INITIAL_AUDIENCES_STATE;
};

const types: Types = {
  AUDIENCE_COMPONENT_ERR,
  AUDIENCE_COMPONENT_OK,
  AUDIENCE_COMPONENTS_ERR,
  AUDIENCE_COMPONENTS_OK,
  AUDIENCE_TOGGLE_UPDATE,
  AUDIENCE_CONFIGURATION_UPDATE,
  REQUEST_AUDIENCE,
  REQUEST_AUDIENCE_ERR,
  REQUEST_AUDIENCE_OK,
  REQUEST_AUDIENCES_OK,
  REQUEST_SEGMENTS,
  REQUEST_SEGMENTS_ERR,
  REQUEST_SEGMENTS_OK,
  RESET_INITIAL_AUDIENCES_STATE,
};

// Audiences
export const IN_ALL_SEGMENTS = 'inAllSegments';
export const IN_ANY_SEGMENTS = 'inAnySegments';
export const AGE_WITHIN = 'ageWithin';
export type AudienceKeys = typeof AGE_WITHIN | typeof IN_ALL_SEGMENTS | typeof IN_ANY_SEGMENTS;

type AgeWithin = {
  active: boolean;
  maximumAge: number | '';
  maximumAgeActive: boolean;
  minimumAge: number | '';
  minimumAgeActive: boolean;
  type: typeof AGE_WITHIN;
};

export type Segment = {
  id: string;
  name: string;
};

type InAllSegments = {
  active: boolean;
  availableSegments?: Segment[];
  type: typeof IN_ALL_SEGMENTS;
  segments: string[];
};

type InAnySegments = {
  active: boolean;
  availableSegments?: Segment[];
  type: typeof IN_ANY_SEGMENTS;
  segments: string[];
};

export type Audiences = AgeWithin | InAllSegments | InAnySegments;

// Actions
type AudienceComponentERR = (
  audienceType: AudienceKeys,
  errorMessages: ErrorMessage[],
  error: Error,
) => {
  type: typeof AUDIENCE_COMPONENT_ERR;
  audienceType: AudienceKeys;
  errorMessages: ErrorMessage[];
  error: Error;
};

type AudienceComponentOK = (
  audienceType: AudienceKeys,
  id: string,
) => {
  type: typeof AUDIENCE_COMPONENT_OK;
  audienceType: AudienceKeys;
  id: string;
};

type AudienceComponentsERR = (
  errorMessages: ErrorMessage[],
  error: Error,
) => {
  type: typeof AUDIENCE_COMPONENTS_ERR;
  errorMessages: ErrorMessage[];
  error: Error;
};

type AudienceComponentsOK = () => {
  type: typeof AUDIENCE_COMPONENTS_OK;
};

type AudienceConfigurationUpdate = (
  audienceConfigurationKey: AudienceKeys,
  key: string,
  value: any,
) => {
  type: typeof AUDIENCE_CONFIGURATION_UPDATE;
  audienceConfigurationKey: AudienceKeys;
  key: string;
  value: any;
};

type AudienceToggleUpdate = (audienceConfigurationKey: AudienceKeys) => {
  type: typeof AUDIENCE_TOGGLE_UPDATE;
  audienceConfigurationKey: AudienceKeys;
};

type RequestAudience = (
  id: string,
  audienceType: AudienceKeys,
) => {
  type: typeof REQUEST_AUDIENCE;
  id: string;
  audienceType: AudienceKeys;
};

type RequestAudienceERR = (
  errorMessages: ErrorMessage[],
  error: Error,
) => {
  type: typeof REQUEST_AUDIENCE_ERR;
  errorMessages: ErrorMessage[];
  error: Error;
};

type RequestAudienceOK = (selectedAudience: Audiences) => {
  type: typeof REQUEST_AUDIENCE_OK;
  selectedAudience: Audiences;
};

type RequestAudiencesOK = () => {
  type: typeof REQUEST_AUDIENCES_OK;
};

type RequestSegments = () => {
  type: typeof REQUEST_SEGMENTS;
};

type RequestSegmentsERR = (
  errorMessages: ErrorMessage[],
  error: Error,
) => {
  type: typeof REQUEST_SEGMENTS_ERR;
  errorMessages: ErrorMessage[];
  error: Error;
};

type RequestSegmentsOK = (segments: Segment[]) => {
  type: typeof REQUEST_SEGMENTS_OK;
  segments: Segment[];
};

type ResetInitialAudiencesState = (audiences: Audiences) => {
  type: typeof RESET_INITIAL_AUDIENCES_STATE;
  audiences: Audiences;
};

export type Action =
  | ReturnType<AudienceComponentERR>
  | ReturnType<AudienceComponentOK>
  | ReturnType<AudienceComponentsERR>
  | ReturnType<AudienceComponentsOK>
  | ReturnType<AudienceConfigurationUpdate>
  | ReturnType<AudienceToggleUpdate>
  | ReturnType<RequestAudience>
  | ReturnType<RequestAudienceERR>
  | ReturnType<RequestAudienceOK>
  | ReturnType<RequestAudiencesOK>
  | ReturnType<RequestSegments>
  | ReturnType<RequestSegmentsERR>
  | ReturnType<RequestSegmentsOK>
  | ReturnType<ResetInitialAudiencesState>;

export type ActionCreators = {
  AudienceComponentERR: AudienceComponentERR;
  AudienceComponentOK: AudienceComponentOK;
  AudienceComponentsERR: AudienceComponentsERR;
  AudienceComponentsOK: AudienceComponentsOK;
  AudienceConfigurationUpdate: AudienceConfigurationUpdate;
  AudienceToggleUpdate: AudienceToggleUpdate;
  RequestAudience: RequestAudience;
  RequestAudienceERR: RequestAudienceERR;
  RequestAudienceOK: RequestAudienceOK;
  RequestAudiencesOK: RequestAudiencesOK;
  RequestSegments: RequestSegments;
  RequestSegmentsERR: RequestSegmentsERR;
  RequestSegmentsOK: RequestSegmentsOK;
  ResetInitialAudiencesState: ResetInitialAudiencesState;
};

// State
type OfferComponentMetaData = {
  dirty: boolean;
  error: Error | null;
  id: string;
  submitting: boolean;
};

type AgeWithinErrorMessage = {
  property?: 'maximum_age' | 'minimum_age';
  message: string;
};

export type AgeWithinState = OfferComponentMetaData &
  AgeWithin & {
    errorMessages: AgeWithinErrorMessage[];
  };

type InAllSegmentsErrorMessage = {
  property?: 'segments';
  message: string;
};

type InAllSegmentsState = OfferComponentMetaData &
  InAllSegments & {
    errorMessages: InAllSegmentsErrorMessage[];
  };

type InAnySegmentsErrorMessage = {
  property?: 'segments';
  message: string;
};

type InAnySegmentsState = OfferComponentMetaData &
  InAnySegments & {
    errorMessages: InAnySegmentsErrorMessage[];
  };

export type State = {
  ageWithin: AgeWithinState;
  inAllSegments: InAllSegmentsState;
  inAnySegments: InAnySegmentsState;
  error: Error | null;
  errorMessages: ErrorMessage[];
  requesting: boolean;
};

export default types;
